import { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik } from 'formik'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { OfferSettingsCard } from '../NewServicesV2/components/OfferSettingsCard'
import { VisualizationSettingsCard } from '../NewServicesV2/components/VisualizationSettingsCard'
import { CapacityLimitsCard } from '../NewServicesV2/components/CapacityLimitsCard'
import { PlanningsCard } from './components/PlanningsCard'
import { ServiceDetailsCard } from './components/ServiceDetailsCard'
import { editServicesSchema } from './schema'
import { getOriginsByItineraries } from 'models/services'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import PeakSettingsField from '../PeakSettings'
import { IEditServiceProps } from 'features/Services/components/EditService/types'
import { SwitchWithLabel } from 'components/v2/Switch'
import { isDate } from 'date-fns'

export const EditService = ({ service, onSubmit, loading, error, canEdit }: IEditServiceProps): ReactElement => {
  const intl = useIntl()
  const isEndDateOptional = !isDate(service.endDate)

  return (
    <Formik
      initialValues={service}
      validationSchema={editServicesSchema(intl, { isEndDateOptional })}
      onSubmit={onSubmit}
    >
      {({ values, handleSubmit, dirty }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <Heading.H3>
              <FormattedMessage id="services.edit.title" defaultMessage="Edit Service" />
            </Heading.H3>
            <ServiceDetailsCard />
            <PlanningsCard oldService={service.services[0]} isEndDateOptional={isEndDateOptional} />
            <OfferSettingsCard isEdit vertical={values.company.vertical} />
            <CapacityLimitsCard isServicePetFriendly={values.isPetFriendly} />
            <VisualizationSettingsCard
              salesChannels={values.salesChannels}
              origins={getOriginsByItineraries(service.services[0].itineraries)}
            />
            <Row>
              <Column>
                <FormLayout.Card>
                  <Heading.H4>
                    <FormattedMessage id="services.form.section.moreSettings.title" defaultMessage="More Settings" />
                  </Heading.H4>
                  <Row>
                    <Column width="50%">
                      <SwitchWithLabel
                        value={values.isOnGuard}
                        label={
                          <FormattedMessage id="services.form.section.moreSettings.onGuard" defaultMessage="On Guard" />
                        }
                        onChange={(): void => undefined}
                        disabled
                      />
                    </Column>
                  </Row>
                  <PeakSettingsField canDelete={false} />
                </FormLayout.Card>
              </Column>
            </Row>
            {error && (
              <FormLayout.Actions>
                <ErrorMessage>
                  <FormattedMessage
                    id="services.edit.serviceForm.error"
                    defaultMessage="There was an error while editing your service"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button as={NavLink} kind="cancel" to="/routes" disabled={loading}>
                {canEdit ? (
                  <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                ) : (
                  <FormattedMessage id="dictionary.close" defaultMessage="Close" />
                )}
              </Button>
              {canEdit && (
                <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
                  <FormattedMessage id="dictionary.save" defaultMessage="Save" />
                </Button>
              )}
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
